import type { Band, Motivation } from '@hmawiw/shared';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { haptic } from '../native/platform';
import { distanceToBox, edgeScroll, indexFromMidpoints, swipeThreshold } from './dropIndex';

export interface DragState {
  id: string;
  motivation: Motivation;
  band: Band;
  index: number;
  height: number;
}

export interface GestureHandlers {
  onMove(id: string, motivation: Motivation, band: Band, index: number): void;
  onSwipe(id: string, direction: 1 | -1): void;
  canSwipe(id: string, direction: 1 | -1): boolean;
}

type Mode = 'drag-wait' | 'hold-wait' | 'drag' | 'swipe-wait' | 'swipe';

interface Gesture {
  mode: Mode;
  pointerId: number;
  touch: boolean;
  id: string;
  cardEl: HTMLElement;
  slotEl: HTMLElement;
  originM: number;
  originB: number;
  x0: number;
  y0: number;
  x: number;
  y: number;
  ghost: HTMLElement | null;
  offX: number;
  offY: number;
  timer: ReturnType<typeof setTimeout> | null;
  raf: number | null;
  last: { m: number; b: number; index: number } | null;
}

const HOLD_MS = 250;
const MOUSE_THRESHOLD = 6;
const CANCEL_HOLD_AFTER = 12;
const SWIPE_START = 12;

function cellOf(el: Element | null): { m: number; b: number } | null {
  const cell = el?.closest<HTMLElement>('[data-cell]');
  if (!cell) return null;
  return { m: Number(cell.dataset['m']), b: Number(cell.dataset['b']) };
}

/**
 * Every card gesture in one place, delegated from the board element.
 * Mouse: press and move a card anywhere. Touch: hold the grip to drag within the column,
 * or swipe the card body sideways to change its column.
 */
export function useCardGestures(boardRef: RefObject<HTMLElement | null>, handlers: GestureHandlers): DragState | null {
  const [drag, setDrag] = useState<DragState | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const suppressClick = useRef(false);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    let g: Gesture | null = null;

    const findCell = (x: number, y: number): HTMLElement | null => {
      const els = document.elementsFromPoint(x, y);
      for (const el of els) if (el instanceof HTMLElement && el.hasAttribute('data-cell')) return el;
      const col = els.find((el) => el instanceof HTMLElement && el.hasAttribute('data-col')) as HTMLElement | undefined;
      if (!col) return null;
      let best: HTMLElement | null = null;
      let bestD = Infinity;
      for (const cell of col.querySelectorAll<HTMLElement>('[data-cell]')) {
        const r = cell.getBoundingClientRect();
        const d = distanceToBox(r.top, r.bottom, y);
        if (d < bestD) {
          bestD = d;
          best = cell;
        }
      }
      return best;
    };

    const updateTarget = () => {
      if (!g || g.mode !== 'drag') return;
      const cell = findCell(g.x, g.y);
      if (!cell) return;
      const m = Number(cell.dataset['m']);
      const b = Number(cell.dataset['b']);
      if (g.touch && m !== g.originM) return;
      const cards = [...cell.querySelectorAll<HTMLElement>('[data-card]')].filter((c) => c.dataset['card'] !== g?.id);
      const mids = cards.map((c) => {
        const r = c.getBoundingClientRect();
        return r.top + r.height / 2;
      });
      const index = indexFromMidpoints(mids, g.y);
      if (g.last && g.last.m === m && g.last.b === b && g.last.index === index) return;
      g.last = { m, b, index };
      const height = g.cardEl.getBoundingClientRect().height || 56;
      setDrag({ id: g.id, motivation: m as Motivation, band: b as Band, index, height });
    };

    const tickScroll = () => {
      if (!g || g.mode !== 'drag') return;
      const col = document.elementsFromPoint(g.x, g.y).find((el) => el instanceof HTMLElement && el.hasAttribute('data-col')) as HTMLElement | undefined;
      if (col) {
        const r = col.getBoundingClientRect();
        const dy = edgeScroll(r.top, r.bottom, g.y);
        if (dy) {
          col.scrollTop += dy;
          updateTarget();
        }
      }
      g.raf = requestAnimationFrame(tickScroll);
    };

    const startDrag = () => {
      if (!g || g.mode === 'drag') return;
      g.mode = 'drag';
      suppressClick.current = true;
      haptic('lift');
      const r = g.cardEl.getBoundingClientRect();
      g.offX = g.x - r.left;
      g.offY = g.y - r.top;
      const ghost = g.cardEl.cloneNode(true) as HTMLElement;
      ghost.removeAttribute('data-card');
      ghost.setAttribute('data-ghost', '');
      ghost.style.position = 'fixed';
      ghost.style.left = '0';
      ghost.style.top = '0';
      ghost.style.width = `${r.width}px`;
      ghost.style.zIndex = '60';
      ghost.style.pointerEvents = 'none';
      ghost.style.transform = `translate(${r.left}px, ${r.top}px) rotate(1.2deg)`;
      ghost.style.boxShadow = 'var(--lift)';
      ghost.style.opacity = '1';
      document.body.appendChild(ghost);
      g.ghost = ghost;
      document.body.classList.add('is-dragging');
      const origin = cellOf(g.cardEl);
      g.last = null;
      setDrag({ id: g.id, motivation: (origin?.m ?? g.originM) as Motivation, band: (origin?.b ?? g.originB) as Band, index: 0, height: r.height });
      updateTarget();
      g.raf = requestAnimationFrame(tickScroll);
    };

    const startSwipe = () => {
      if (!g) return;
      g.mode = 'swipe';
      suppressClick.current = true;
      g.cardEl.style.transition = 'none';
    };

    const paintSwipe = () => {
      if (!g || g.mode !== 'swipe') return;
      let dx = g.x - g.x0;
      const dir: 1 | -1 = dx > 0 ? 1 : -1;
      if (!handlersRef.current.canSwipe(g.id, dir)) dx = dx / 4;
      g.cardEl.style.transform = `translateX(${dx}px)`;
      g.slotEl.dataset['swipe'] = Math.abs(dx) > SWIPE_START ? (dir > 0 ? 'right' : 'left') : '';
    };

    const endSwipe = (commit: boolean) => {
      if (!g) return;
      const card = g.cardEl;
      const slot = g.slotEl;
      const dx = g.x - g.x0;
      const dir: 1 | -1 = dx > 0 ? 1 : -1;
      const width = card.getBoundingClientRect().width || 300;
      const ok = commit && Math.abs(dx) >= swipeThreshold(width) && handlersRef.current.canSwipe(g.id, dir);
      const id = g.id;
      card.style.transition = 'transform 160ms ease-out';
      if (ok) {
        haptic('confirm');
        card.style.transform = `translateX(${dir * (width + 40)}px)`;
        setTimeout(() => {
          card.style.transition = '';
          card.style.transform = '';
          slot.dataset['swipe'] = '';
          handlersRef.current.onSwipe(id, dir);
        }, 150);
      } else {
        card.style.transform = 'translateX(0)';
        setTimeout(() => {
          card.style.transition = '';
          card.style.transform = '';
          slot.dataset['swipe'] = '';
        }, 170);
      }
    };

    const cleanup = () => {
      if (!g) return;
      if (g.timer) clearTimeout(g.timer);
      if (g.raf) cancelAnimationFrame(g.raf);
      if (g.ghost) g.ghost.remove();
      document.body.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey);
      const wasDrag = g.mode === 'drag';
      g = null;
      if (wasDrag) setDrag(null);
      setTimeout(() => {
        suppressClick.current = false;
      }, 0);
    };

    const onMove = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.pointerId) return;
      g.x = e.clientX;
      g.y = e.clientY;
      const dx = g.x - g.x0;
      const dy = g.y - g.y0;
      const dist = Math.hypot(dx, dy);
      switch (g.mode) {
        case 'drag-wait':
          if (dist > MOUSE_THRESHOLD) startDrag();
          break;
        case 'hold-wait':
          if (dist > CANCEL_HOLD_AFTER) cleanup();
          break;
        case 'drag':
          e.preventDefault();
          if (g.ghost) g.ghost.style.transform = `translate(${g.x - g.offX}px, ${g.y - g.offY}px) rotate(1.2deg)`;
          updateTarget();
          break;
        case 'swipe-wait':
          if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) cleanup();
          else if (Math.abs(dx) > SWIPE_START) {
            startSwipe();
            paintSwipe();
          }
          break;
        case 'swipe':
          e.preventDefault();
          paintSwipe();
          break;
      }
    };

    const onUp = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.pointerId) return;
      if (g.mode === 'drag') {
        const target = g.last;
        if (target) handlersRef.current.onMove(g.id, target.m as Motivation, target.b as Band, target.index);
      } else if (g.mode === 'swipe') {
        endSwipe(true);
      }
      cleanup();
    };

    const onCancel = (e: PointerEvent) => {
      if (!g || e.pointerId !== g.pointerId) return;
      if (g.mode === 'swipe') endSwipe(false);
      cleanup();
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && g) {
        if (g.mode === 'swipe') endSwipe(false);
        cleanup();
      }
    };

    const onDown = (e: PointerEvent) => {
      if (g) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const target = e.target as Element | null;
      const cardEl = target?.closest<HTMLElement>('[data-card]');
      if (!cardEl || !board.contains(cardEl)) return;
      if (target?.closest('button, input, textarea, a, select')) return;
      const slotEl = cardEl.closest<HTMLElement>('[data-slot]') ?? cardEl;
      const touch = e.pointerType !== 'mouse';
      const onGrip = !!target?.closest('[data-grip]');
      const origin = cellOf(cardEl);
      if (!origin) return;
      const id = cardEl.dataset['card'] as string;
      let mode: Mode;
      if (touch && onGrip) mode = 'hold-wait';
      else if (touch) mode = 'swipe-wait';
      else mode = 'drag-wait';
      g = {
        mode,
        pointerId: e.pointerId,
        touch,
        id,
        cardEl,
        slotEl,
        originM: origin.m,
        originB: origin.b,
        x0: e.clientX,
        y0: e.clientY,
        x: e.clientX,
        y: e.clientY,
        ghost: null,
        offX: 0,
        offY: 0,
        timer: mode === 'hold-wait' ? setTimeout(startDrag, HOLD_MS) : null,
        raf: null,
        last: null,
      };
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('keydown', onKey);
    };

    const onClickCapture = (e: MouseEvent) => {
      if (suppressClick.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    const onContextMenu = (e: Event) => {
      if (g || (e.target as Element | null)?.closest('[data-grip]')) e.preventDefault();
    };

    board.addEventListener('pointerdown', onDown);
    board.addEventListener('click', onClickCapture, true);
    board.addEventListener('contextmenu', onContextMenu);
    return () => {
      board.removeEventListener('pointerdown', onDown);
      board.removeEventListener('click', onClickCapture, true);
      board.removeEventListener('contextmenu', onContextMenu);
      cleanup();
    };
  }, [boardRef]);

  return drag;
}
